<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import { APPT_STATUSES, FIELD_CLASS, SELECT_CLASS } from '../constants';
import { apiErrorMessage } from '../../../../api/realEstate/errors';
import { isProjectDeleted } from '../helpers';

const LOCATION_TYPES = ['showroom', 'property', 'virtual', 'office'];

// Date/time never change here — that's reschedule's job. Everything else
// PATCH /properties/appointments/{id} allows: agent (agent rows only —
// PATCHing it on a walk-in row is a 422 agent_not_applicable), notes,
// the handover checklist and the location (docs/real-estate/be/
// 10-api-reference.md §A13).
//
// Status is here too, sent separately via PUT …/status: PRD §6 lets staff
// move a booking between any statuses (e.g. reopen a cancelled one), while
// the table's quick buttons only follow the normal forward path.
export default {
  name: 'AppointmentEditModal',
  props: {
    appointment: {
      type: Object,
      required: true,
    },
  },
  data() {
    const a = this.appointment;
    return {
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      locationTypes: LOCATION_TYPES,
      apptStatuses: APPT_STATUSES,
      status: a.status,
      cancelReason: '',
      agentId: a.agent ? a.agent.agent_id : '',
      locationType: a.location_type,
      locationNote: a.location_note || '',
      customerNotes: a.customer_notes || '',
      agentNotes: a.agent_notes || '',
      handoverChecklist: a.handover_checklist
        ? { ...a.handover_checklist }
        : {
            payment_proof_received: false,
            signed_agreement_on_file: false,
          },
      isSubmitting: false,
    };
  },
  computed: {
    ...mapGetters('realEstate', { getProject: 'getProject' }),
    project() {
      return this.getProject(this.appointment.property.property_id);
    },
    isAgentRow() {
      return this.appointment.booking_mode === 'agent';
    },
    // The project is gone, so its agent list can't be loaded and the agent
    // can't be changed (409 property_deleted) — the current one is shown.
    projectDeleted() {
      return isProjectDeleted(this.appointment);
    },
    agentOptions() {
      if (this.project && !this.projectDeleted) return this.project.agents;
      return this.appointment.agent ? [this.appointment.agent] : [];
    },
    isKeyHandover() {
      return this.appointment.appointment_type === 'key_handover';
    },
    isStatusChanged() {
      return this.status !== this.appointment.status;
    },
  },
  mounted() {
    if (this.isAgentRow && !this.projectDeleted) {
      this.fetchProject(this.appointment.property.property_id);
    }
  },
  methods: {
    ...mapActions('realEstate', ['fetchProject']),
    ...mapActions('realEstateAppointments', [
      'patchAppointment',
      'setAppointmentStatus',
    ]),
    async handleSubmit() {
      this.isSubmitting = true;
      try {
        const fields = {
          location_type: this.locationType,
          location_note: this.locationNote || null,
          customer_notes: this.customerNotes || null,
          agent_notes: this.agentNotes || null,
        };
        const currentAgentId = this.appointment.agent
          ? this.appointment.agent.agent_id
          : '';
        if (this.isAgentRow && this.agentId !== currentAgentId) {
          fields.agent_id = this.agentId;
        }
        if (this.isKeyHandover) {
          fields.handover_checklist = { ...this.handoverChecklist };
        }
        await this.patchAppointment({
          id: this.appointment.appointment_id,
          fields,
        });
        if (this.isStatusChanged) {
          await this.setAppointmentStatus({
            id: this.appointment.appointment_id,
            status: this.status,
            reason:
              this.status === 'cancelled'
                ? this.cancelReason.trim() || null
                : null,
          });
        }
        useAlert(this.$t('REAL_ESTATE.APPOINTMENTS.SUCCESS_UPDATED'));
        this.$emit('close');
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'REAL_ESTATE.APPOINTMENTS.ERROR_UPDATE')
        );
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <div
    class="fixed inset-0 flex items-center justify-center bg-slate-800/80 z-50"
  >
    <div
      class="bg-white dark:bg-slate-900 rounded-lg p-6 shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto"
    >
      <div class="flex items-center mb-4 justify-between">
        <h1 class="text-xl font-bold text-slate-900 dark:text-slate-100">
          {{ $t('REAL_ESTATE.APPOINTMENTS.MODAL_EDIT_TITLE') }}
        </h1>
        <woot-button
          variant="clear"
          color-scheme="alert"
          icon="dismiss"
          @click="$emit('close')"
        />
      </div>

      <form class="space-y-4" @submit.prevent="handleSubmit">
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.APPOINTMENTS.FIELD_STATUS') }}
          </label>
          <select v-model="status" :class="selectClass">
            <option
              v-for="(label, key) in apptStatuses"
              :key="key"
              :value="key"
            >
              {{ label }}
            </option>
          </select>
        </div>
        <div v-if="isStatusChanged && status === 'cancelled'">
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.APPOINTMENTS.FIELD_CANCEL_REASON') }}
          </label>
          <input
            v-model="cancelReason"
            :placeholder="
              $t('REAL_ESTATE.APPOINTMENTS.FIELD_CANCEL_REASON_PLACEHOLDER')
            "
            type="text"
            class="reset-base mb-0"
            :class="fieldClass"
          />
        </div>

        <div v-if="isAgentRow">
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.APPOINTMENTS.FIELD_AGENT') }}
          </label>
          <select
            v-model="agentId"
            :class="selectClass"
            :disabled="projectDeleted"
          >
            <option
              v-for="agent in agentOptions"
              :key="agent.agent_id"
              :value="agent.agent_id"
            >
              {{ agent.name }}
            </option>
          </select>
          <p
            v-if="projectDeleted"
            class="mt-1 mb-0 text-xs text-slate-500 dark:text-slate-400"
          >
            {{ $t('REAL_ESTATE.APPOINTMENTS.AGENT_PROJECT_DELETED') }}
          </p>
        </div>

        <div
          v-if="isKeyHandover"
          class="border-b border-slate-200 dark:border-slate-700 pb-3"
        >
          <p
            class="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
          >
            {{ $t('REAL_ESTATE.APPOINTMENTS.CHECKLIST_TITLE') }}
          </p>
          <label
            class="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 mb-1"
          >
            <input
              v-model="handoverChecklist.payment_proof_received"
              type="checkbox"
            />
            {{ $t('REAL_ESTATE.APPOINTMENTS.CHECKLIST_PAYMENT') }}
          </label>
          <label
            class="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300"
          >
            <input
              v-model="handoverChecklist.signed_agreement_on_file"
              type="checkbox"
            />
            {{ $t('REAL_ESTATE.APPOINTMENTS.CHECKLIST_AGREEMENT') }}
          </label>
        </div>

        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.APPOINTMENTS.FIELD_LOCATION_TYPE') }}
          </label>
          <select v-model="locationType" :class="selectClass">
            <option v-for="type in locationTypes" :key="type" :value="type">
              {{
                $t(
                  `REAL_ESTATE.APPOINTMENTS.LOCATION_TYPE_${type.toUpperCase()}`
                )
              }}
            </option>
          </select>
        </div>
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.APPOINTMENTS.FIELD_LOCATION_NOTE') }}
          </label>
          <input
            v-model="locationNote"
            :placeholder="
              $t('REAL_ESTATE.APPOINTMENTS.FIELD_LOCATION_NOTE_PLACEHOLDER')
            "
            type="text"
            :class="fieldClass"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.APPOINTMENTS.FIELD_CUSTOMER_NOTES') }}
          </label>
          <textarea
            v-model="customerNotes"
            :placeholder="
              $t('REAL_ESTATE.APPOINTMENTS.FIELD_CUSTOMER_NOTES_PLACEHOLDER')
            "
            rows="2"
            :class="fieldClass"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.APPOINTMENTS.FIELD_AGENT_NOTES') }}
          </label>
          <textarea
            v-model="agentNotes"
            :placeholder="
              $t('REAL_ESTATE.APPOINTMENTS.FIELD_AGENT_NOTES_PLACEHOLDER')
            "
            rows="2"
            :class="fieldClass"
          />
        </div>

        <div
          class="flex justify-end gap-3 pt-4 mt-2 border-t border-slate-200 dark:border-slate-700"
        >
          <woot-button variant="clear" type="button" @click="$emit('close')">
            {{ $t('REAL_ESTATE.APPOINTMENTS.CANCEL') }}
          </woot-button>
          <woot-button type="submit" :is-loading="isSubmitting">
            {{ $t('REAL_ESTATE.APPOINTMENTS.SAVE') }}
          </woot-button>
        </div>
      </form>
    </div>
  </div>
</template>
